import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client for the La Folie Coach Portal.
 *
 * Week 0 reality: there is no live Supabase project yet. Rather than crashing
 * the whole app on a missing key, the shell reports that it is unconfigured and
 * runs on the bundled demo dataset. Any code path that genuinely needs the
 * backend calls requireSupabase(), which throws a message that names the exact
 * variables to set.
 */

const rawUrl = import.meta.env.VITE_SUPABASE_URL
const rawKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export const supabaseUrl = typeof rawUrl === 'string' ? rawUrl.trim() : ''
export const supabaseAnonKey = typeof rawKey === 'string' ? rawKey.trim() : ''

/** Public VAPID key for web push. Without it the app cannot create a subscription. */
export const vapidPublicKey =
  typeof import.meta.env.VITE_VAPID_PUBLIC_KEY === 'string'
    ? import.meta.env.VITE_VAPID_PUBLIC_KEY.trim()
    : ''

/** Set VITE_DEMO_MODE=true to force the bundled dataset even with real keys. */
const forcedDemo = String(import.meta.env.VITE_DEMO_MODE ?? '').toLowerCase() === 'true'

export const isSupabaseConfigured =
  !forcedDemo && supabaseUrl.length > 0 && supabaseAnonKey.length > 0

export const MISSING_ENV_MESSAGE = [
  'Supabase is not configured.',
  'Copy .env.example to .env.local and set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY,',
  'then restart the dev server. Values live in the La Folie Supabase project under',
  'Project Settings > API. Never commit them.',
].join(' ')

let client: SupabaseClient | null = null

if (isSupabaseConfigured) {
  client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      // Coaches are part-time and open the app rarely. Long-lived sessions are
      // a deliberate decision, see CLAUDE.md.
      storageKey: 'lafolie-coach-portal-auth',
    },
    global: {
      headers: { 'x-application-name': 'lafolie-coach-portal' },
    },
  })
}

/** The client, or null when running on the bundled demo dataset. */
export const supabase = client

/** The client, or a thrown error explaining precisely what to configure. */
export function requireSupabase(): SupabaseClient {
  if (!client) throw new Error(MISSING_ENV_MESSAGE)
  return client
}

/** Small helper so query call sites stay readable. */
export function unwrap<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message)
  if (result.data === null) throw new Error('Query returned no data')
  return result.data
}
